import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Text, Title } from '@factorialco/deprecated-design-system'
import {
  CreatePolicyLimitExtractionDocument,
  PolicyLimitExtractionPageDocument,
} from 'generated/resources/graphql'

import DropZone from 'modules/core/components/DropZone'
import NotFound from 'modules/core/components/NotFound'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

type UploadStatus = 'idle' | 'uploading' | 'success' | 'error'

interface ResultState {
  status: UploadStatus
  message?: string
  limitDetected?: boolean
  limitAmount?: number | null
  limitFrequency?: string | null
}

const PolicyLimitExtractionPage = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { company, employee } = useCurrentSession()

  const { data, loading } = useQuery(PolicyLimitExtractionPageDocument, {
    variables: {
      companyId: company.id,
      featureNames: ['dev_expenses_policy_settings'],
    },
  })

  const [result, setResult] = React.useState<ResultState>({ status: 'idle' })

  const [createPolicyLimitExtraction] = useMutation(CreatePolicyLimitExtractionDocument, {
    extractorFn: (x) => x.expensesMutations.createExpensesPolicyLimitExtraction,
  })

  const handleDrop = React.useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return

      const file = acceptedFiles[0]
      if (!file) return

      // Validate PDF
      if (file.type !== 'application/pdf') {
        setResult({
          status: 'error',
          message: 'Please upload a PDF file',
        })
        return
      }

      setResult({ status: 'uploading' })

      try {
        const response = await createPolicyLimitExtraction({
          variables: {
            companyId: company.id,
            authorId: employee.id,
            file: file,
          },
        })

        let errorMessage: string | null = null

        const handled = handleResult(response, (formErrors) => {
          // Extract error messages from structured errors
          const errorMessages = Object.values(formErrors).flat()
          if (errorMessages.length > 0) {
            errorMessage = errorMessages.join('. ')
          }
        })

        if (!handled.success) {
          setResult({
            status: 'error',
            message: errorMessage || 'Failed to process the policy document',
          })
          return
        }

        const extractionResult = handled.data?.policyLimitExtraction

        if (!extractionResult) {
          setResult({
            status: 'error',
            message: 'No response received from the server',
          })
          return
        }

        if (!extractionResult.limitDetected) {
          setResult({
            status: 'success',
            limitDetected: false,
            message:
              'No spending limit for meals was detected in this policy document. Please try with a different document or configure the workflow manually.',
          })
          return
        }

        if (!extractionResult.workflowId) {
          setResult({
            status: 'error',
            message: 'A limit was detected but the workflow could not be created',
          })
          return
        }

        // Success - redirect to the workflow page
        setResult({
          status: 'success',
          limitDetected: true,
          limitAmount: extractionResult.limitAmount,
          limitFrequency: extractionResult.limitFrequency,
        })

        // Navigate to the workflow page with the workflow ID
        navigate(
          `${navigation.settings.expenses.policyCategoryWorkflows.fullPath()}?workflowId=${
            extractionResult.workflowId
          }`
        )
      } catch (error) {
        setResult({
          status: 'error',
          message: 'An unexpected error occurred while processing the document',
        })
      }
    },
    [company.id, employee.id, createPolicyLimitExtraction, navigate, navigation]
  )

  const isFeatureEnabled =
    data?.features?.featuresConnection?.nodes?.find(
      (feature) => feature.name === 'dev_expenses_policy_settings'
    )?.enabled ?? false

  if (loading) {
    return (
      <ResponsivePage.Body loading error={null} showHeader={false} variant="fullscreen">
        <></>
      </ResponsivePage.Body>
    )
  }

  if (!isFeatureEnabled) {
    return <NotFound />
  }

  const isUploading = result.status === 'uploading'

  return (
    <ResponsivePage.Body showHeader={false} variant="fullscreen">
      <Box alignItems="center" flexDirection="column" gap="s32" paddingX="s64" paddingY="s48">
        <Box alignItems="center" flexDirection="column" gap="s16">
          <Title title="Extract policy limits from your document" variant="400" />
          <Text color="grey700" size="400">
            Upload your expense policy document (PDF) and we&apos;ll automatically extract the
            spending limits for meals and create a workflow to enforce them.
          </Text>
        </Box>

        <DropZone
          accept={{ 'application/pdf': ['.pdf'] }}
          disabled={isUploading}
          loading={isUploading}
          multiple={false}
          placeholder="Drop a file or click to upload"
          subtitle="PDF, up to 10 MB size"
          onDrop={handleDrop}
        />

        {result.status === 'error' && result.message && (
          <Box
            background="radicalRed100"
            borderRadius={{ all: 'abs008' }}
            paddingX="s24"
            paddingY="s16"
          >
            <Text color="radicalRed1200" size="300">
              {result.message}
            </Text>
          </Box>
        )}

        {result.status === 'success' && !result.limitDetected && result.message && (
          <Box
            background="champagne100"
            borderRadius={{ all: 'abs008' }}
            paddingX="s24"
            paddingY="s16"
          >
            <Text color="champagne1000" size="300">
              {result.message}
            </Text>
          </Box>
        )}
      </Box>
    </ResponsivePage.Body>
  )
}

export default PolicyLimitExtractionPage
